import { FileSearch, RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { queryKeys } from "@/api/hooks";
import { classifyRunReadFailure, usePreviewRunEventQuery } from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { formatDateTime } from "@/lib/format";
import { CONTENT_UNAVAILABLE_LABELS } from "@/lib/previewInsights";
import {
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  INVOCATION_LABELS,
  VISIBILITY_LABELS,
  describeInvocation,
  readPromptCallDetail,
  type PromptCall,
  type PromptCallDetailReading,
} from "@/lib/previewPromptCall";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { ExactPromptCall } from "@/ui/preview/ExactPromptCall";
import { PromptProvenanceLayers } from "@/ui/preview/PromptProvenanceLayers";
import { ErrorState } from "@/ui/states";

import { Fact, Identifier, PreviewReadFailureState } from "./previewRunParts";

/**
 * Preview Insights — ONE prompt call, exactly (#1099).
 *
 * The detail pane of the Prompt Inspector (`PreviewRunPromptInspector.tsx`,
 * #1100): the call a clinician selected from the navigation, shown exactly —
 * the effective system prompt, the messages sent in order, the structured
 * result and the patient-facing answer where the producer recorded them, the
 * model and runtime identity, the timestamp, and whether the model was invoked
 * at all.
 *
 * ## The body is loaded on demand, and only here
 *
 * `GET …/events/{eventRef}` runs when the clinician presses the load control
 * and at no other time. It is the one read in this repository that carries a
 * prompt body, and the Editor Service audits it as "full prompt detail
 * viewed". Nothing polls it: the run page's timer re-reads the envelope, and
 * the navigation's timer re-reads compact metadata.
 *
 * ## The layers beside the prompt (#1101)
 *
 * `PromptProvenanceLayers` renders the ordered provenance layers of this same
 * call beneath the exact prompt: which named layer contributed which bytes, who
 * authored each, and — for a layer that came from a clinical document — a link
 * to the Editor section where that document is authored. It makes its own
 * bounded read and never composes a prompt: the prompt on this page is the
 * call's own `systemPrompt`, and the layers explain it rather than rebuild it.
 *
 * ## What this module deliberately is not
 *
 * Not a list, not a filter, not a decision card. The navigation is #1100's,
 * next door. This renders ONE call.
 *
 * ## Content is text, and only text (ADR 0013 N4; design §7.4)
 *
 * Every body reaches the screen as a text node inside its own block. Nothing
 * in it is interpreted as markup, a link or a control: an HTML tag is
 * characters, a URL is characters, a script is characters. Each block is
 * direction-isolated (`dir="auto"`, `unicode-bidi: plaintext`) so a
 * right-to-left prompt reads right-to-left inside a left-to-right page and
 * vice versa, and so nothing inside a body can reorder the chrome around it.
 * The Unicode bidirectional control characters — the ones that reorder text
 * silently — are each rendered inside their own isolated, visibly marked
 * element and kept in the text, so the body stays byte-exact while no control
 * in it can reach its neighbours.
 *
 * ## Absence is a state, never "nothing happened" (ADR 0013 N7)
 *
 * A body the producer withheld for size, a body policy withheld at source, a
 * call in which the model was never invoked, a call that failed, and an event
 * this build cannot read are five different sentences below, and none is
 * rendered as an empty prompt.
 */
/**
 * ONE prompt call: its compact record, and its exact bodies once a clinician
 * asks for them. The Prompt Inspector's detail pane renders it for the call
 * selected from the navigation.
 */
export function PromptCallCard({
  call,
  projectId,
  previewRunRef,
  title,
}: {
  call: PromptCall;
  projectId: string;
  previewRunRef: string;
  /** What this card is, in the surface that renders it. */
  title: string;
}) {
  const [requested, setRequested] = useState(false);
  const detail = usePreviewRunEventQuery(projectId, previewRunRef, call.eventRef, {
    enabled: requested,
  });
  const exactHeadingId = useId();
  const exactHeadingRef = useRef<HTMLHeadingElement>(null);
  // FOCUS FOLLOWS THE ANSWER a keyboard user asked for. The load control is
  // replaced by the loaded content, so without this focus would fall to
  // `body` and a screen reader would announce nothing. Only after a press —
  // a later refetch keeps focus where the clinician left it.
  const focusOnArrival = useRef(false);
  useEffect(() => {
    if (detail.isSuccess && focusOnArrival.current) {
      focusOnArrival.current = false;
      exactHeadingRef.current?.focus();
    }
  }, [detail.isSuccess]);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {/* The producer's one-line label: clinical text, so it chooses its own direction. */}
          <p dir="auto" className="mt-0.5 text-sm text-muted-foreground">
            {call.label ?? "Label not reported"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={EVENT_STATUS_TONES[call.status]}>
            {EVENT_STATUS_LABELS[call.status]}
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-6">
        <CallFacts call={call} />

        <section aria-labelledby={exactHeadingId} className="space-y-3">
          <h3
            id={exactHeadingId}
            ref={exactHeadingRef}
            tabIndex={-1}
            className="text-sm font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            Exact prompt
          </h3>
          {!requested ? (
            <>
              <p className="text-sm text-muted-foreground">
                The exact system prompt, the messages sent and the model result
                are loaded only when you ask. The record above carries their
                sizes and digests, never their text. Everything loaded is
                rendered as text: nothing in it is interpreted as markup, a
                link or a control.
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  focusOnArrival.current = true;
                  setRequested(true);
                }}
              >
                <FileSearch aria-hidden="true" className="h-4 w-4" />
                Load exact prompt
              </Button>
            </>
          ) : detail.isPending || !detail.isFetchedAfterMount ? (
            <QueryPendingState
              isPaused={detail.isPaused}
              failureReason={detail.failureReason}
              label="Loading the exact prompt"
              errorTitle="The exact prompt could not be loaded"
              rows={3}
              queryKey={queryKeys.previewRunEvent(projectId, previewRunRef, call.eventRef)}
            />
          ) : detail.isError ? (
            <PreviewReadFailureState
              subject="prompt_call"
              failure={classifyRunReadFailure(detail.error)}
              error={detail.error}
              onRetry={() => void detail.refetch()}
            />
          ) : (
            <LoadedPromptCall
              reading={readPromptCallDetail(detail.data, call.eventRef)}
              onReload={() => void detail.refetch()}
              reloading={detail.isFetching}
            />
          )}
        </section>

        {/*
          #1101. Its own section and its own read, BENEATH the prompt: the
          layers explain the prompt above them, and reading them costs no
          prompt text of its own until a clinician asks for it.
        */}
        <PromptProvenanceLayers
          call={call}
          projectId={projectId}
          previewRunRef={previewRunRef}
        />
      </CardBody>
    </Card>
  );
}

/**
 * The compact record — every fact the locator carries without a body: call
 * type, position in the run, timestamp, visibility class, invocation outcome
 * and the model that answered. Identifiers keep their own left-to-right
 * order inside a right-to-left page.
 */
function CallFacts({ call }: { call: PromptCall }) {
  const { coordinate, invocation, modelIdentity } = call;
  return (
    <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
      <Fact label="Call type">
        {call.callType !== null ? <Identifier value={call.callType} /> : "Not reported"}
      </Fact>
      <Fact label="Recorded at">
        {call.occurredAt !== null ? (
          <time dateTime={call.occurredAt} dir="ltr">
            {formatDateTime(call.occurredAt)}
          </time>
        ) : (
          "Not reported"
        )}
      </Fact>
      <Fact label="Sequence">
        {call.sequence !== null ? <span dir="ltr">Event {call.sequence}</span> : "Not reported"}
      </Fact>
      <Fact label="Turn">
        {coordinate.turnNumber !== null ? (
          <span dir="ltr">{coordinate.turnNumber}</span>
        ) : (
          "Not applicable"
        )}
      </Fact>
      <Fact label="Position">
        <CoordinateLine
          parts={[
            ["Phase", coordinate.phase],
            ["Track", coordinate.track],
            ["Agent", coordinate.agent],
            ["Step", coordinate.step],
          ]}
        />
      </Fact>
      <Fact label="Visibility">{VISIBILITY_LABELS[call.visibility]}</Fact>
      <Fact label="Model invocation">
        <Badge
          tone={
            invocation.outcome === "invoked"
              ? "success"
              : invocation.outcome === "failed"
                ? "destructive"
                : "neutral"
          }
        >
          {INVOCATION_LABELS[invocation.outcome]}
        </Badge>
        <span className="mt-1 block text-xs text-muted-foreground">
          {describeInvocation(invocation)}
        </span>
      </Fact>
      <Fact label="Model">
        {modelIdentity?.model ? (
          <Identifier value={modelIdentity.model} />
        ) : invocation.outcome === "not_invoked" ? (
          "No model was invoked"
        ) : (
          "Not reported"
        )}
      </Fact>
      <Fact label="Model deployment">
        {modelIdentity?.modelDeployment ? (
          <Identifier value={modelIdentity.modelDeployment} />
        ) : invocation.outcome === "not_invoked" ? (
          "No model was invoked"
        ) : (
          "Not reported"
        )}
      </Fact>
    </dl>
  );
}

function CoordinateLine({ parts }: { parts: [string, string | null][] }) {
  const stated = parts.filter((part): part is [string, string] => part[1] !== null);
  if (stated.length === 0) {
    return <>Not applicable</>;
  }
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {stated.map(([name, value]) => (
        <span key={name} className="inline-flex items-baseline gap-1">
          <span className="text-xs text-muted-foreground">{name}</span>
          <Identifier value={value} />
        </span>
      ))}
    </span>
  );
}

/** The detail read, once it answered — checked against the event it was asked for. */
function LoadedPromptCall({
  reading,
  onReload,
  reloading,
}: {
  reading: PromptCallDetailReading;
  onReload: () => void;
  reloading: boolean;
}) {
  switch (reading.kind) {
    case "content_unavailable":
      return (
        <ErrorState
          title="This run's trace has no readable content"
          description={
            reading.run.contentUnavailableReason
              ? `The Editor Service reports that ${CONTENT_UNAVAILABLE_LABELS[reading.run.contentUnavailableReason]}. This call's bodies cannot be shown.`
              : "The Editor Service reports no readable content for this run and names no reason. This call's bodies cannot be shown."
          }
        />
      );
    case "unreadable":
      return (
        <ErrorState
          title="This prompt call is not one this build can read"
          description={`The Editor Service relayed an event this build cannot place (at ${reading.at}). Nothing is shown rather than a partial prompt.`}
        />
      );
    case "mismatch":
      return (
        <ErrorState
          title="The Editor Service answered a different event"
          description="The event that came back is not the one this panel addressed. Nothing is shown rather than a prompt from elsewhere in the run."
        />
      );
    case "readable":
      return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Loaded on request from the recorded preview target. Rendered as
              text only.
            </p>
            <Button variant="outline" size="sm" onClick={onReload} disabled={reloading}>
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Reload
            </Button>
          </div>
          <ExactPromptCall call={reading.call} />
        </div>
      );
  }
}

/** The exact call: its bodies, each in its own state, in the order the model saw them. */
